
import React, { useState, useEffect } from 'react';
import ImageUpload from '../../../sharedcomponents/imageupload/imageupload';
import { fetchApi } from '../../../services/api';
import { buildD7Payload } from '../../../services/payloads/d7Payload';
import { getCurrentUserEmail } from '../../../services/auth';
import '../d1/d1.css';
import '../d2/d2.css';
import '../d7/d7.css';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Button,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TwoMonthDatePicker from '../../../sharedcomponents/two-month-datepicker/TwoMonthDatePicker';

/* -----------------------------
 * Helpers
 * --------------------------- */
const toIsoOrNull = (date) => {
  if (!date) return null;
  try {
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
};

const D7 = ({ expanded, onSetClick, handleCloseForm, onToggleAccordion, isEnabled = false, onSaved, teamMembers = [], eightDInfoId, currentUserEmail = getCurrentUserEmail() || sessionStorage.getItem('userEmail') || '', d7Data, d7ResponsibleData, mode, readOnly = false }) => {
  // When readOnly is true, disable all edit interactions
  const effectiveEnabled = isEnabled && !readOnly;
  const canView = isEnabled;
  /* Header states */
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [plannedDate, setPlannedDate] = useState(null);
  const [achievedDate, setAchievedDate] = useState(null);
  const [errors, setErrors] = useState({ person: '', planned: '', achieved: '' });

  /* D7 content */
  const [holisticView, setHolisticView] = useState('');

  // Actions table (horizontal deployment / standard updates)
  const [actionRows, setActionRows] = useState([
    { action: '', responsible: '', date: null, status: '' }
  ]);

  /* Attachments & removal arrays */
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]);
  const [actionToRemove, setActionToRemove] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);

  // Save state & notices
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveNotice, setSaveNotice] = useState(null);
  const [d7Id, setD7Id] = useState(null);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [formSaved, setFormSaved] = useState(false);
  const [isResponsibleSet, setIsResponsibleSet] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAchievedDate, setShowAchievedDate] = useState(false);
  const [formClosed, setFormClosed] = useState(true);

  // Determine if current user is the responsible person for this form
  const isCurrentUserResponsible = responsiblePerson && currentUserEmail && 
    responsiblePerson.toLowerCase() === currentUserEmail.toLowerCase();
  // Block form when user is not originator (readOnly) and not the responsible person
  const isFormBlocked = readOnly ? !isCurrentUserResponsible : false;

  // Check if responsible person is already set (only when d7Id exists, meaning Set was clicked)
  useEffect(() => {
    if (d7Id) {
      setIsResponsibleSet(true);
    }
  }, [d7Id]);

  // Fetch D7 data on mount if eightDInfoId exists
  useEffect(() => {
    // Fetch from API if eightDInfoId exists and no props data
    if (eightDInfoId && !d7Data && !d7ResponsibleData && mode === 'edit') {
      const fetchD7Data = async () => {
        try {
          const response = await fetchApi('/api/D7/GetD7', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
          });
          
          if (response?.data) {
            const d7 = response.data;
            if (d7.D7Id || d7.d7Id) { setD7Id(d7.D7Id || d7.d7Id); setFormSaved(true); }
            if (d7.ResponsiblePerson) setResponsiblePerson(d7.ResponsiblePerson);
            if (d7.PlannedDate) setPlannedDate(new Date(d7.PlannedDate));
            if (d7.AchievedDate) setAchievedDate(new Date(d7.AchievedDate));
            if (d7.HolisticView) setHolisticView(d7.HolisticView);
            if (d7.Action && Array.isArray(d7.Action) && d7.Action.length > 0) {
              setActionRows(d7.Action.map(item => ({
                id: item.D7ActionId || item.d7ActionId || item.Id || item.id || null,
                action: item.Action || '',
                responsible: item.Responsible || '',
                date: item.TargetDate ? new Date(item.TargetDate) : null,
                status: item.Status || ''
              })));
            }
            
            if (d7.Attachments && Array.isArray(d7.Attachments)) {
              const existingFiles = d7.Attachments.map(att => ({
                ...att,
                isExisting: true,
                preview: att.AttachmentData
              }));
              setUploadedFiles(existingFiles);
            }
          }
        } catch (error) {
        }
      };
      fetchD7Data();
      return;
    }
    
    // Load from props first (edit mode from reports)
    if (d7Data) {
      const data = Array.isArray(d7Data) ? d7Data[0] : d7Data;
      
      if (data) {
        if (data.D7Id || data.d7Id) { setD7Id(data.D7Id || data.d7Id); setFormSaved(true); }
        if (data.ResponsiblePerson) setResponsiblePerson(data.ResponsiblePerson);
        if (data.PlannedDate) setPlannedDate(new Date(data.PlannedDate));
        if (data.AchievedDate) setAchievedDate(new Date(data.AchievedDate));
        if (data.HolisticView) setHolisticView(data.HolisticView);
        
        if (data.Action && Array.isArray(data.Action) && data.Action.length > 0) {
          setActionRows(data.Action.map(item => ({
            id: item.D7ActionId || item.d7ActionId || item.Id || item.id || null,
            action: item.Action || '',
            responsible: item.Responsible || '',
            date: item.TargetDate ? new Date(item.TargetDate) : null,
            status: item.Status || ''
          })));
        }
        
        if (data.Attachments && Array.isArray(data.Attachments)) {
          const existingFiles = data.Attachments.map(att => ({
            ...att,
            isExisting: true,
            preview: att.AttachmentData
          }));
          setUploadedFiles(existingFiles);
        }
      }
    }
    
    if (d7ResponsibleData) {
      const respData = Array.isArray(d7ResponsibleData) ? d7ResponsibleData[0] : d7ResponsibleData;
      if (respData) {
        if (respData.ResponsiblePerson) setResponsiblePerson(respData.ResponsiblePerson);
        if (respData.PlannedDate) setPlannedDate(new Date(respData.PlannedDate));
        if (respData.AchievedDate) setAchievedDate(new Date(respData.AchievedDate));
      }
    }
  }, [eightDInfoId, d7Data, d7ResponsibleData]);

  const handleActionRowChange = (index, field, value) => {
    setActionRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
    setIsFormTouched(true);
  };
  const maxActionRows = 3;
  const addActionRow = () => {
    setActionRows((prev) => prev.length < maxActionRows ? [...prev, { action: '', responsible: '', date: null, status: '' }] : prev);
    setIsFormTouched(true);
  };
  const removeActionRow = (index) => {
    const removed = actionRows[index];
    if (removed?.id) {
      setActionToRemove(prev => [...prev, removed.id]);
    }
    setActionRows((prev) => prev.filter((_, i) => i !== index));
    setIsFormTouched(true);
  };

  const validateField = (field, value) => {
    let error = '';
    if (field === 'person' && !value) error = 'Responsible person is required';
    if (field === 'planned' && !value) error = 'Planned date is required';
    if (field === 'achieved' && !value) error = 'Achieved date is required';
    setErrors((prev) => ({ ...prev, [field]: error }));
  };
  const handlePersonChange = (value) => {
    setResponsiblePerson(value);
    validateField('person', value);
  };
  const handlePlannedChange = (value) => {
    setPlannedDate(value);
    validateField('planned', value);
  };
  const handleAchievedChange = (value) => {
    setAchievedDate(value);
    validateField('achieved', value);
  };


  const handleUpdateResponsiblePerson = async () => {
    if (!responsiblePerson) return;
    
    setSaveError(null);
    setSaveNotice(null);

    if (!eightDInfoId) {
      setSaveError('eightDInfoId is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        D7Id: d7Id || undefined,
        EightDInfoId: eightDInfoId,
        ResponsiblePerson: responsiblePerson,
        PlannedDate: plannedDate?.toISOString(),
        AchievedDate: achievedDate?.toISOString()
      };

      const endpoint = d7Id ? '/api/D7/UpdateD7Responsible' : '/api/D7/CreateD7';
      const response = await fetchApi(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response?.data?.D7Id || response?.data?.d7Id) {
        setD7Id(response.data.D7Id || response.data.d7Id);
      }

      setIsResponsibleSet(true);
      setFormClosed(false);
      setSaveNotice(d7Id ? "Updated D7 form successfully." : "D7 responsible person set successfully.");
      setTimeout(() => setSaveNotice(null), 3000);
    } catch (e) {
      setSaveError(e?.message ?? 'Failed to set responsible person.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  /* -----------------------------
   * Save handler
   * --------------------------- */
  const handleSave = async () => {
    setSaveError(null);
    setSaveNotice(null);
    setSaving(true);
    
    try {
      const payload = buildD7Payload({
        eightDInfoId,
        holisticView,
        responsiblePerson,
        plannedDate,
        achievedDate,
        actionRows,
        uploadedFiles,
        isDisabled,
        attachmentsToRemove,
        actionToRemove,
        currentUserEmail: currentUserEmail || responsiblePerson,
        d7Id,
      });

      const endpoint = d7Id ? '/api/D7/UpdateD7' : '/api/D7/CreateD7';
      const data = await fetchApi(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (data?.data?.D7Id) {
        if (data?.data?.D7Id || data?.data?.d7Id) setD7Id(data.data.D7Id || data.data.d7Id);
      }

      // Re-fetch to get assigned child IDs
      try {
        const refreshed = await fetchApi('/api/D7/GetD7', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
        });
        if (refreshed?.data) {
          const d7 = refreshed.data;
          if (d7.Action?.length > 0) {
            setActionRows(d7.Action.map(item => ({
              id: item.D7ActionId || null, action: item.Action || '',
              responsible: item.Responsible || '', date: item.TargetDate ? new Date(item.TargetDate) : null, status: item.Status || ''
            })));
          }
          if (d7.Attachments?.length > 0) {
            setUploadedFiles(d7.Attachments.map(att => ({ ...att, isExisting: true, preview: att.AttachmentData })));
          }
        }
      } catch (e) { }

      setActionToRemove([]);
      setAttachmentsToRemove([]);
      setSaveNotice('D7 prevent recurrence saved successfully.');
      setTimeout(() => setSaveNotice(null), 2000);
      setFormSaved(true);

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      setSaveError(error?.message || 'Failed to save D7 prevent recurrence.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  const toggleAccordion = () => {
    if (readOnly && isEnabled) {
      onToggleAccordion();
      return;
    }
    if (effectiveEnabled && isEditMode && (d7Id || (responsiblePerson && plannedDate))) {
      onToggleAccordion();
    }
  };

  const handleReset = () => {
    setHolisticView('');
    setActionRows([{ action: '', responsible: '', date: null, status: '' }]);
    setUploadedFiles([]);
    setAttachmentsToRemove([]);
    setActionToRemove([]);
    setSaveError(null);
    setSaveNotice(null);
  };

  /* -----------------------------
   * Render
   * --------------------------- */
  return (
    <>
      <Box className={`d3-header ${canView ? 'enabled' : 'disabled'}`} onClick={toggleAccordion} style={{ cursor: canView ? 'pointer' : 'not-allowed', pointerEvents: canView ? 'auto' : 'none', opacity: canView ? 1 : 0.5 }}>
        {/* Left Section */}
        <Box className="d3-left-section">
          <Box className="d3-circle" style={achievedDate && formSaved ? { backgroundColor: '#4ccb91', borderColor: '#4ccb91' } : (responsiblePerson || isEditMode || formSaved ? { backgroundColor: '#f8c239', borderColor: '#f8c239' } : {})} />
          <span className="d3-title">D7</span>
          <span className="d3-subtitle">Prevent Recurrence</span>
          <Tooltip
            title={
              <span>
                Implement containment to control the situation and mitigate further negative effects of the
                problem. (segregation, selection, temporary actions in the process etcâ€¦..)
              </span>
            }
            arrow
            placement="bottom-start"
            componentsProps={{ tooltip: { className: 'custom-tooltip' }, arrow: { className: 'custom-arrow' } }}
          >
            <IconButton><InfoOutlinedIcon sx={{ color: '#666' }} /></IconButton>
          </Tooltip>
        </Box>

        {/* Responsible Person */}
        <Box className="responsible-box" onClick={(e) => e.stopPropagation()}>
          <Select
            value={responsiblePerson}
            onChange={(e) => handlePersonChange(e.target.value)}
            displayEmpty
            variant="standard"
            className="responsible-select"
            error={!!errors.person}
            disabled={!effectiveEnabled || isFormBlocked}
            sx={{
              '& .MuiInput-underline:after': {
                borderBottomColor: '#800040',
              },
              '& .MuiInput-underline:before': {
                borderBottomColor: '#ccc',
              },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottomColor: '#800040',
              }
            }}
          >
            <MenuItem value="" sx={{ display: 'none' }}>Responsible Person</MenuItem>
            {responsiblePerson && !teamMembers.some(m => (m.EmailId || m.emailId) === responsiblePerson) && (
              <MenuItem value={responsiblePerson}>{responsiblePerson}</MenuItem>
            )}
            {teamMembers.map((member) => (
              <MenuItem key={member.D1Id} value={member.EmailId || member.emailId}>
                {member.TeamMemberName || member.teamMemberName}
              </MenuItem>
            ))}
          </Select>
          {errors.person && <Typography className="error-text">{errors.person}</Typography>}
        </Box>

        {/* Planned Date */}
        <Box onClick={(e) => e.stopPropagation()}>
          <TwoMonthDatePicker
            value={plannedDate}
            onChange={(value) => handlePlannedChange(value)}
            label="Planned Date"
            disabled={!effectiveEnabled || isFormBlocked || (isResponsibleSet && !isEditMode)}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: '#777' } }}
          />
        </Box>

        {/* Achieved Date - enabled when responsible person is set */}
        <Box onClick={(e) => {
          e.stopPropagation();
          if (isResponsibleSet && !expanded) {
            setIsEditMode(true);
            setFormClosed(false);
            setIsFormTouched(false);
            onToggleAccordion();
          }
        }}>
          <TwoMonthDatePicker
            value={achievedDate}
            onChange={(d) => {
              setAchievedDate(d);
              setIsFormTouched(true);
              if (!isEditMode) {
                setIsEditMode(true);
                onToggleAccordion();
              }
            }}
            label="Achieved Date"
            disabled={!isResponsibleSet || isFormBlocked}
            maxDate={new Date()}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: isEditMode && !achievedDate ? '#d32f2f' : '#777' } }}
            sx={isEditMode && !achievedDate ? {
              '& .MuiInput-underline:before': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:after': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#d32f2f' },
              '& .MuiInputLabel-root': { color: '#d32f2f !important' },
            } : {}}
          />
        </Box>

        {/* Edit/Update Buttons */}
        {isResponsibleSet ? (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || isFormBlocked}
              onClick={() => { setIsEditMode(true); setFormClosed(false); onToggleAccordion(); }}
              sx={{ minWidth: '60px', fontSize: '0.75rem' }}
            >
              Edit
            </Button>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || isFormBlocked || !responsiblePerson || !plannedDate} 
              onClick={handleUpdateResponsiblePerson}
              sx={{ minWidth: '70px', fontSize: '0.75rem' }}
            >
              Update
            </Button>
          </Box>
        ) : (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()}>
            <Button variant="outlined" disabled={!effectiveEnabled || isFormBlocked || !responsiblePerson || !plannedDate} onClick={handleUpdateResponsiblePerson}>Set</Button>
          </Box>
        )}
      </Box>

      {/* Success/Error Messages */}
      {(saveNotice || saveError) && (
        <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, minWidth: '300px' }}>
          {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
          {saveNotice && <Alert severity="success" onClose={() => setSaveNotice(null)}>{saveNotice}</Alert>}
        </Box>
      )}

      {/* Accordion Form */}
      {expanded && (isEditMode || readOnly) && (
        <Accordion expanded={expanded} sx={{ mt: 2 }}>
          <AccordionSummary>
            <Box className="accordion-close-button-container">
              <Button
                className="close-button"
                variant="contained"
                color="secondary"
                startIcon={<CloseIcon />}
                onClick={() => { setIsEditMode(false); setFormClosed(true); handleCloseForm(); }}
              >
                Close Form
              </Button>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={isFormBlocked ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
            {/* Q1: Scope / Actions (horizontal deployment) */}
            <Box className="question-block">
              <Box className="question-label">
                <span className="d2-qnum">Q1</span>
                <span className="d2-question-text">Scope for horizontal deployment</span>
              </Box>
              {actionRows.map((row, index) => (
                <Box key={`act-${index}`} className="q1-container">
                  <TextField
                    variant="standard"
                    label="Action"
                    value={row.action}
                    onChange={(e) => handleActionRowChange(index, 'action', e.target.value)}
                    fullWidth
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: '#777' } }}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  />
                  <Select
                    value={row.responsible}
                    onChange={(e) => handleActionRowChange(index, 'responsible', e.target.value)}
                    displayEmpty
                    variant="standard"
                    error={isFormTouched && (!!row.date || !!row.action?.trim()) && !row.responsible}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: isFormTouched && (row.date || row.action?.trim()) && !row.responsible ? '#d32f2f' : '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: isFormTouched && (row.date || row.action?.trim()) && !row.responsible ? '#d32f2f' : '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: isFormTouched && (row.date || row.action?.trim()) && !row.responsible ? '#d32f2f' : '#800040',
                      },
                      '& .MuiSelect-select': {
                        color: isFormTouched && (row.date || row.action?.trim()) && !row.responsible ? '#d32f2f' : 'inherit',
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ display: 'none' }}>Responsible Person</MenuItem>
                    {row.responsible && !teamMembers.some(m => m.TeamMemberName === row.responsible) && (
                      <MenuItem value={row.responsible}>{row.responsible}</MenuItem>
                    )}
                    {teamMembers.map((member) => (
                      <MenuItem key={member.D1Id} value={member.TeamMemberName}>
                        {member.TeamMemberName}
                      </MenuItem>
                    ))}
                  </Select>

                  <TwoMonthDatePicker
                    value={row.date}
                    onChange={(date) => handleActionRowChange(index, 'date', date)}
                    label={isFormTouched && (row.responsible || row.action?.trim()) && !row.date ? 'Target Date *' : 'Target Date'}
                    error={isFormTouched && (!!row.responsible || !!row.action?.trim()) && !row.date}
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: isFormTouched && (row.responsible || row.action?.trim()) && !row.date ? '#d32f2f' : '#777' } }}
                    sx={{
                      '& .MuiInput-underline:before': { borderBottomColor: isFormTouched && (row.responsible || row.action?.trim()) && !row.date ? '#d32f2f' : '#ccc' },
                      '& .MuiInput-underline:after': { borderBottomColor: isFormTouched && (row.responsible || row.action?.trim()) && !row.date ? '#d32f2f' : '#800040' },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: isFormTouched && (row.responsible || row.action?.trim()) && !row.date ? '#d32f2f' : '#800040' },
                    }}
                  />

                  <Select
                    value={row.status}
                    onChange={(e) => handleActionRowChange(index, 'status', e.target.value)}
                    displayEmpty
                    variant="standard"
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  >
                    <MenuItem value="">Status</MenuItem>
                    <MenuItem value="Complete">Complete</MenuItem>
                    <MenuItem value="Inprocess">Inprocess</MenuItem>
                  </Select>

                  {index === actionRows.length - 1 && actionRows.length < maxActionRows ? (
                    <IconButton onClick={addActionRow} className="q1-add-btn"><AddIcon /></IconButton>
                  ) : (
                    <IconButton onClick={() => removeActionRow(index)} className="q1-remove-btn"><RemoveIcon /></IconButton>
                  )}
                </Box>
              ))}
            </Box>

            {/* Q2: Holistic view / Standard updates */}
            <Box className="question-block">
              <Box className="question-label">
                <span className="d2-qnum">Q2</span>
                <span className="d2-question-text">Updation of standard / any other action</span>
              </Box>
              <Box className="customTextarea col-sm-5 col-12" mt={2}>
                <Typography component="label" gutterBottom>
                  Can this problem occur on similar product? <br />
                  Can this problem occur on similar line? <br />
                  Who is assigned to address this issue?
                </Typography>
                <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
                  <InputLabel shrink htmlFor="holistic-view"></InputLabel>
                  <TextField
                    className="customTextarea"
                    id="holistic-view"
                    name="holisticView"
                    value={holisticView}
                    onChange={(e) => { setHolisticView(e.target.value); setIsFormTouched(true); }}
                    multiline
                    variant="standard"
                    InputProps={{ disableUnderline: false}}
                    fullWidth
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  />
                </FormControl>                
              </Box>
            </Box>

            {/* Attachments */}
            <ImageUpload onFilesSelected={(files) => {
              uploadedFiles.forEach(ef => {
                const efId = ef.AttachmentId || ef.attachmentId;
                if (efId && !files.some(f => (f.AttachmentId || f.attachmentId) === efId)) {
                  setAttachmentsToRemove(prev => prev.includes(efId) ? prev : [...prev, efId]);
                }
              });
              setUploadedFiles(files);
              setIsFormTouched(true);
            }} folderPath="D7" existingFiles={uploadedFiles} maxFiles={3} rejectedExtensions={['doc', 'docx']} />

            {/* Save & Reset Buttons */}
            <Box className="save-btn-container" sx={{ textAlign: 'center', mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button 
                variant="contained" 
                className="save-btn" 
                onClick={handleSave}
                disabled={saving || !isFormTouched || isFormBlocked}
                sx={{
                  backgroundColor: isFormTouched && !isFormBlocked ? '#800040' : '#ccc',
                  color: '#fff',
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 4,
                  cursor: isFormTouched ? 'pointer' : 'not-allowed'
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button 
                variant="contained" 
                onClick={handleReset}
                sx={{
                  backgroundColor: '#800040',
                  color: '#fff',
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 4
                }}
              >
                Reset
              </Button>
            </Box>

            {/* Save notices */}
            {saveError && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{saveError}</Alert>
              </Box>
            )}
            {saveNotice && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success">{saveNotice}</Alert>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default D7;
